import { AppDrawerSelector } from "entities/app-drawer/selector";
import { IPersonalAccountPartsVisibility } from "entities/app-drawer/types/app-drawer-interface";
import { fetchCheckAuth } from "entities/protected-route/action/fetch-check-auth";
import { IsValidSession, LoadingCheckSession } from "entities/protected-route/selector";
import { useEffect, useState, FC, ReactElement, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import LoaderVerify from "shared/loader-verify/LoaderVerify";
import { RoutesType } from "shared/types/routes/routers-type";
import { useAppDispatch, useAppSelector } from "shared/utils/hooks/useRedux";

interface IProtectedRoute {
  onlyAuth?: boolean;
  component: ReactElement;
}
export const ProtectedRoute: FC<IProtectedRoute> = ({ component }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isValidSession = useAppSelector(IsValidSession);
  const isLoading = useAppSelector(LoadingCheckSession);
  const appDrawerPermissions = useAppSelector(AppDrawerSelector);

  const [hasChecked, setHasChecked] = useState(false);
  const [showLoader, setShowLoader] = useState(true); 
  const redirectTriggeredRef = useRef(false); 

  const mapRoute = {
    Statistics: RoutesType.Statistics,
    InsuranceContracts: RoutesType.ContractsList,
    InsurancePrograms: RoutesType.ProgramsList,
    ListOfInsuredPersons: RoutesType.InsuredPersons,
    Applications: RoutesType.TasksList,
    InsuranceOfRelatives: RoutesType.InsuranceOfRelatives,
    BonusProgram: RoutesType.BonusProgram,
    Projects: RoutesType.Projects,
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const verify = async () => {
      try {
        await dispatch(fetchCheckAuth()).unwrap();
      } catch {
      } finally {
        setHasChecked(true);
      }
    };
    verify();
  }, [dispatch, location.pathname]);

  // Проверка маршрута
  const getRouteKeyByPath = (path: string): keyof typeof mapRoute | null => {
    for (const [key, route] of Object.entries(mapRoute)) {
      if (route === path) return key as keyof typeof mapRoute;
    }
    return null;
  };

  const currentPath = location.pathname;
  const currentRouteKey = getRouteKeyByPath(currentPath);
  const isRouteAllowed =
    currentRouteKey === null ||
    appDrawerPermissions?.[currentRouteKey as keyof IPersonalAccountPartsVisibility] !== false;

  useEffect(() => {
    if (showLoader) return; 

    if (!hasChecked || isLoading) return;

    if (!isValidSession) {
      if (!redirectTriggeredRef.current) {
        redirectTriggeredRef.current = true;
        navigate(RoutesType.Auth);
      }
    } else if (!isRouteAllowed) {
      if (!redirectTriggeredRef.current) {
        redirectTriggeredRef.current = true;
        navigate(RoutesType.ContractsList);
      }
    }
  }, [showLoader, hasChecked, isLoading, isValidSession, isRouteAllowed, navigate]);

  if (showLoader || isLoading || !hasChecked) {
    return <LoaderVerify title="Идет проверка учетных данных. Пожалуйста, подождите." />;
  }

  if (!isValidSession) {
    return null;
  }

  return component;
};